import axios, { AxiosInstance } from "axios";
import store from "../store";
import acquireTokenSilent from "../components/auth/acquireTokenSilent";
import acquireTokenInteractive from "../components/auth/acquireTokenInteractive";
import { API_URL } from "@env";

import { reportError } from "../utils/Instrumentation";
import { redirectToLogin, isAuthError } from "../utils/authRedirect";
import { tokenAcquisitionSuccess } from "../store/slices/authSlice";
import { setPeriodWeek, setLastPeriodWeekFetchDate } from "../store/slices/appDataSlice";
import { logger } from "../utils/logger";
import { 
  initializeSessionLockService, 
  getSessionLockService, 
  LockAcquireRequest,
  SessionLockService 
} from "./sessionLockService";
import { shouldAcquireLock, getLockConfig } from "./sessionLockConfig";
// import { getBaseUrlForEndpoint } from "./apiRoutingConfig";
import { 
  showSessionLockConflictModal, 
  setActiveLock,
  clearActiveLock,
  showLockExpirationWarning,
  hideLockExpirationWarning,
  showLockAcquisitionError 
} from "../store/slices/sessionLockSlice";

// Define __DEV__ if it doesn't exist (for web environments)
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";


/**
 * Helper function to extract the token value from either web or native token response
 * @param token The token object or string returned by MSAL
 * @returns The access token string
 */
const getTokenValue = (token: any): string | null => {
  if (!token) {
    return null;
  }

  // For web, token is a string
  if (typeof token === "string") {
    return token;
  }

  // For native platforms, token is an object with accessToken property
  if (token.accessToken) {
    return token.accessToken;
  }

  return null;
};


// Global lock to prevent concurrent period-week API calls
let isPeriodWeekFetching = false;
let periodWeekFetchPromise: Promise<void> | null = null;

/**
 * Helper function to get current date in local timezone as YYYY-MM-DD
 * @returns Current date in local timezone formatted as YYYY-MM-DD
 */
export const getCurrentLocalDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getLocalTimestamp = (): string => {
  const now = new Date();

  // Date components (using local getters)
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  // Time components (using local getters)
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0'); 

  // Combine into the desired format (without the 'Z')
  const formattedLocalTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;

  logger.log(formattedLocalTime);
  return formattedLocalTime;
};

/**
 * Normalize deprecated timezone aliases to IANA canonical names
 * @param timezone The timezone string from Intl API or other source
 * @returns Normalized timezone name
 */
const normalizeTimezone = (timezone: string): string => {
  const aliasMap: Record<string, string> = {
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Ulaanbaatar': 'Asia/Ulan_Bator',
  };
  return aliasMap[timezone] || timezone;
};

/**
 * Helper function to get user's timezone offset in minutes
 * @returns Timezone offset in minutes (e.g., 330 for IST UTC+5:30, -360 for CST UTC-6:00)
 */
export const getTimezoneOffset = (): number => {
  const now = new Date();
  return now.getTimezoneOffset() * -1; // Negate because getTimezoneOffset returns opposite sign
};

/**
 * Helper function to format timezone offset as string (e.g., "UTC+5:30" or "UTC-6:00")
 * @returns Timezone offset string
 */
export const getTimezoneOffsetString = (): string => {
  const offsetMinutes = getTimezoneOffset();
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');
  return `UTC${sign}${hoursStr}:${minutesStr}`;
};
/**
 * Helper function to check if period-week API should be called
 * @returns boolean indicating whether period-week API should be fetched
 */
const shouldFetchPeriodWeek = (): boolean => {
  const state = store.getState();
  const lastPeriodWeekFetchDate = state.appData.lastPeriodWeekFetchDate;
  // Get current date in LOCAL timezone (not UTC) for proper comparison
  const currentDate = getCurrentLocalDate();
  logger.log('current date (local timezone) and lastPeriodWeek date is', currentDate, lastPeriodWeekFetchDate)
  // If no previous fetch date is stored, fetch period-week (first time or after reset)
  if (!lastPeriodWeekFetchDate) {
    logger.log("No previous period-week fetch date found, need to fetch period-week API");
    return true;
  }
  
  // Compare current date with stored date
  if (currentDate !== lastPeriodWeekFetchDate) {
    logger.log(`Date changed from ${lastPeriodWeekFetchDate} to ${currentDate}, need to fetch period-week API`);
    return true;
  }
  
  logger.log(`Same date as last fetch (${currentDate}), skipping period-week API`);
  return false;
};

/**
 * Function to fetch period-week data
 * @param api The axios instance to use for the API call
 * @returns Promise that resolves when period-week data is fetched and stored
 */
const fetchPeriodWeekIfNeeded = async (api: any): Promise<void> => {
  // Skip if this is the period-week API call itself to prevent infinite loop
  if (api.defaults.baseURL && api.defaults.baseURL.includes('period-week')) {
    return;
  }

  // Check if we need to fetch period-week
  if (!shouldFetchPeriodWeek()) {
    return;
  }

  // If already fetching, wait for the existing fetch to complete
  if (isPeriodWeekFetching && periodWeekFetchPromise) {
    logger.log("Period-week API fetch already in progress, waiting for it to complete");
    await periodWeekFetchPromise;
    return;
  }

  // Set the lock and create the fetch promise
  isPeriodWeekFetching = true;
  periodWeekFetchPromise = (async () => {
    try {
      logger.log("Fetching period-week API before proceeding with request");
      const clientTimestamp = getLocalTimestamp()
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const periodWeekUrl = `/common/period-week?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`;
      
      // Make the period-week API call
      const response = await api.get(periodWeekUrl);
      
      // Update Redux store with the fetched data
      store.dispatch(setPeriodWeek(response.data));
      
      // Update the last fetch date using LOCAL timezone (not UTC)
      const currentDate = getCurrentLocalDate();
      store.dispatch(setLastPeriodWeekFetchDate(currentDate));
      
      logger.log("Period-week API fetched successfully, proceeding with original request");
    } catch (error) {
      logger.error("Error fetching period-week API:", error);
      // Don't throw the error, just log it and continue with the original request
      // This ensures the original API call still proceeds even if period-week fails
    } finally {
      // Release the lock
      isPeriodWeekFetching = false;
      periodWeekFetchPromise = null;
    }
  })();

  await periodWeekFetchPromise;
};

/**
 * Acquire session lock for the API request if needed
 * @param api API client instance
 * @param config Axios request config
 * @returns Lock token if lock was acquired, null otherwise
 */
const acquireSessionLockIfNeeded = async (
  api: AxiosInstance,
  config: any
): Promise<{ resourceId: string; lockToken: string } | null> => {
  try {
    // Strip query parameters from URL for endpoint matching
    const fullUrl = config.url || '';
    const endpoint = fullUrl.split('?')[0];
    const method = (config.method || 'GET').toUpperCase();

    logger.log('[SessionLock] Checking if lock required for:', endpoint, method);

    // Check if this endpoint requires a lock
    if (!shouldAcquireLock(endpoint, method)) {
      logger.log('[SessionLock] Lock not required for this endpoint:', endpoint, method);
      return null;
    }

    logger.log('[SessionLock] Endpoint requires lock:', endpoint, method);

    // Get lock configuration
    const lockConfig = getLockConfig(endpoint, method);
    if (!lockConfig) {
      logger.warn('[SessionLock] No lock config found for endpoint:', endpoint);
      return null;
    }

    logger.log('[SessionLock] Lock config found:', lockConfig.module, lockConfig.submodule);

    // Extract lock parameters
    const lockParams = lockConfig.extractLockParams(config);
    if (!lockParams) {
      logger.warn('[SessionLock] Could not extract lock parameters for:', endpoint);
      logger.warn('[SessionLock] Request body:', config.data);
      logger.warn('[SessionLock] Request params:', config.params);
      return null;
    }

    logger.log('[SessionLock] Lock parameters extracted:', lockParams);

    // Initialize session lock service if needed
    let lockService = getSessionLockService();
    if (!lockService) {
      lockService = initializeSessionLockService(api);
    }
    
    // CRITICAL: Check for existing active lock first
    // If a lock already exists for this resource, use the CURRENT (refreshed) lock token
    // This prevents acquiring duplicate locks and ensures we use the latest token
    const existingLockParams = {
      module: lockConfig.module,
      submodule: lockConfig.submodule,
      ...lockParams,
    };
    const existingLock = lockService.getExistingLockByParams(existingLockParams);
    
    if (existingLock) {
      logger.log('[SessionLock] ✅ REUSING existing lock - using refreshed token');
      logger.log('[SessionLock]   Resource ID:', existingLock.resourceId);
      logger.log('[SessionLock]   Token:', existingLock.lockData.lockToken?.substring(0, 20) + '...');
      logger.log('[SessionLock]   Expires at:', existingLock.lockData.expiresAt);
      
      // Add the CURRENT (possibly refreshed) lock token to request
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        if (!config.data) {
          config.data = {};
        }
        if (typeof config.data === 'object' && config.data !== null && !Array.isArray(config.data)) {
          config.data.lockToken = existingLock.lockData.lockToken;
          logger.log('[SessionLock] Existing lock token added to request body');
        }
      } else if (method === 'GET') {
        if (!config.params) {
          config.params = {};
        }
        config.params.lockToken = existingLock.lockData.lockToken;
        logger.log('[SessionLock] Existing lock token added to query params');
      }
      
      return {
        resourceId: existingLock.resourceId,
        lockToken: existingLock.lockData.lockToken,
      };
    }
    
    // No existing lock found - acquire a new one
    logger.log('[SessionLock] No existing lock found, acquiring new lock');
    
    // ALWAYS set up callbacks before acquiring lock (override any previous callbacks)
    // This ensures fresh callbacks for each lock acquisition
    lockService.setExpirationWarningCallback((resourceId: string, lockData: any) => {
      logger.log('[SessionLock] Expiration warning triggered, showing modal');
      console.log('🟡🟡🟡 apiClient EXPIRATION WARNING CALLBACK CALLED 🟡🟡🟡');
      console.log('Dispatching with:', {
        resourceId,
        expiresAt: lockData.expiresAt,
        lockToken: lockData.lockToken,
      });
      store.dispatch(showLockExpirationWarning({
        resourceId,
        expiresAt: lockData.expiresAt,
        lockToken: lockData.lockToken,
      }));
      console.log('🟡🟡🟡 apiClient EXPIRATION WARNING DISPATCH COMPLETE 🟡🟡🟡');
    });

    // Set up callback for lock released (dismiss warning popup)
    lockService.setLockReleasedCallback((resourceId: string) => {
      logger.log('[SessionLock] Lock released callback triggered, hiding warning popup for:', resourceId);
      store.dispatch(hideLockExpirationWarning());
    });

    // Set up callback for acquisition failure
    lockService.setAcquisitionFailureCallback((error: any) => {
      logger.error('[SessionLock] Lock acquisition failed:', error);
      store.dispatch(showLockAcquisitionError({
        error: error.message || 'Failed to acquire lock',
        lockRequest: error.lockRequest,
      }));
    });

    // Prepare lock acquisition request
    const lockRequest: LockAcquireRequest = {
      module: lockConfig.module,
      submodule: lockConfig.submodule,
      ...lockParams,
      sessionId: '', // Will be set by service
    };

    // CRITICAL: Check if we already have an active lock for this resource
    // If so, use the LATEST token from the service (which may have been refreshed by heartbeat)
    const existingLocks = lockService.getActiveLocks();
    let lockData: any = null;
    let isExistingLock = false;
    
    // Find an existing lock that matches this request's resource
    for (const [existingResourceId, existingLockData] of existingLocks.entries()) {
      // Check if this lock matches the current request's parameters
      // For DLO Schedule, match on module/submodule and locId/period/week/year
      const resourceMatches = existingResourceId.includes(`${lockConfig.module}-${lockConfig.submodule}`) &&
        (lockParams.locId ? existingResourceId.includes(`LOC-${lockParams.locId}`) : true) &&
        (lockParams.period !== undefined ? existingResourceId.includes(`P${lockParams.period}`) : true) &&
        (lockParams.week ? existingResourceId.includes(`W${lockParams.week}`) : true) &&
        (lockParams.year ? existingResourceId.includes(`Y${lockParams.year}`) : true);
      
      if (resourceMatches) {
        logger.log('[SessionLock] 🔑 Found EXISTING lock for this resource, using latest token');
        logger.log('[SessionLock]   Resource ID:', existingResourceId);
        logger.log('[SessionLock]   Current lockToken:', existingLockData.lockToken?.substring(0, 20) + '...');
        logger.log('[SessionLock]   Expires at:', existingLockData.expiresAt);
        
        lockData = {
          ...existingLockData,
          resourceId: existingResourceId,
        };
        isExistingLock = true;
        break;
      }
    }
    
    // Only acquire a new lock if no existing lock was found
    if (!lockData) {
      logger.log('[SessionLock] No existing lock found, acquiring new lock');
      lockData = await lockService.acquireLock(lockRequest);
    }

    // Ensure resourceId is set (will be generated by service if not from backend)
    const resourceId = lockData.resourceId || '';
    if (!resourceId) {
      throw new Error('Failed to generate or acquire resource ID');
    }

    // Only update Redux if this is a newly acquired lock (not existing)
    // For existing locks, Redux already has the data
    if (!isExistingLock) {
      // Store lock information in Redux
      store.dispatch(setActiveLock({
        resourceId: resourceId,
        lockData,
      }));
    }

    // Add lock token to request body for methods that support it (POST, PUT, DELETE, PATCH)
    // CRITICAL: Use the LATEST lockToken which may have been refreshed by heartbeat
    const latestLockToken = lockData.lockToken;
    logger.log('[SessionLock] 🔑 Using lockToken for API request:', latestLockToken?.substring(0, 20) + '...');
    
    // This avoids CORS preflight issues with custom headers
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      // Initialize data object if it doesn't exist
      if (!config.data) {
        config.data = {};
      }
      // Add lock token to body if data is an object
      if (typeof config.data === 'object' && config.data !== null && !Array.isArray(config.data)) {
        config.data.lockToken = latestLockToken;
        logger.log('[SessionLock] Lock token added to request body');
      }
    } else if (method === 'GET') {
      // For GET requests, add to query params
      if (!config.params) {
        config.params = {};
      }
      config.params.lockToken = latestLockToken;
      logger.log('[SessionLock] Lock token added to query params');
    }

    logger.log('[SessionLock] Lock acquired and token added to request');
    logger.log('[SessionLock] Token:', latestLockToken?.substring(0, 20) + '...');
    logger.log('[SessionLock] Resource ID:', resourceId);
    logger.log('[SessionLock] Expires at:', lockData.expiresAt);
    logger.log('[SessionLock] Using existing lock:', isExistingLock);

    return {
      resourceId: resourceId,
      lockToken: latestLockToken,
    };
  } catch (error: any) {
    // Check for lock conflict (423 Locked)
    if (error.response?.status === 423 || error.lockConflict) {
      const conflictData = error.lockConflict || error.response?.data;
      logger.warn('[SessionLock] Lock conflict detected:', conflictData);

      // Show lock conflict modal
      store.dispatch(showSessionLockConflictModal({
        isLocked: conflictData.isLocked,
        alreadyLocked: conflictData.alreadyLocked,
        lockedBy: conflictData.lockedBy,
        lockedByName: conflictData.lockedByName,
        lockedAt: conflictData.lockedAt,
        expiresAt: conflictData.expiresAt,
        resourceType: 'Schedule',
      }));

      // Reject request with lock conflict error
      const lockError = new Error('Resource is locked by another user');
      (lockError as any).isLockConflict = true;
      (lockError as any).response = error.response;
      throw lockError;
    }

    // Log other lock errors and trigger acquisition error modal
    logger.warn('[SessionLock] Error acquiring lock:', error);
    
    // Trigger acquisition failure callback if service exists
    const lockService = getSessionLockService();
    if (lockService && lockService['onAcquisitionFailure']) {
      const errorWithRequest = {
        ...error,
        message: error.message || 'Failed to acquire lock',
      };
      (lockService as any).onAcquisitionFailure?.(errorWithRequest);
    }
    
    return null;
  }
};

/**
 * Release session lock after API request completes
 */
const releaseSessionLockIfNeeded = async (
  resourceId: string,
  lockToken: string
): Promise<void> => {
  try {
    const lockService = getSessionLockService();
    if (lockService && resourceId && lockToken) {
      await lockService.releaseLock(resourceId, lockToken);
      
      // Remove lock from Redux
      store.dispatch(clearActiveLock(resourceId));
      
      logger.log('[SessionLock] Lock released for resource:', resourceId);
    }
  } catch (error) {
    logger.warn('[SessionLock] Error releasing lock:', error);
    // Don't throw - lock release errors shouldn't break the app
  }
};

/**
 * Map inspection_module to the appropriate PDF export endpoint
 * @param inspectionModule The inspection module from query params
 * @returns The appropriate backend endpoint path, or null for default core endpoint
 */
const getPdfExportRoute = (inspectionModule: string | null): string | null => {
  const nhoModules = ['nho_certificate', 'nho_prob', 'nho_pulse'];
  const riseQaModules = ['racking_inspection', 'site_evaluation', 'quality_audit'];
  
  if (nhoModules.includes(inspectionModule || '')) {
    return '/notifications/nho/email/pdf';
  }
  
  if (riseQaModules.includes(inspectionModule || '')) {
    return '/notifications/riseqa/email/pdf';
  }
  
  return null; // Use default core endpoint
};

/**
 * React Native-safe query param reader for environments where URLSearchParams
 * may be partially implemented.
 */
const getQueryParamValue = (url: string, paramName: string): string | null => {
  const queryString = url.split('?')[1];
  if (!queryString) {
    return null;
  }

  const pairs = queryString.split('&');
  for (const pair of pairs) {
    if (!pair) {
      continue;
    }

    const separatorIndex = pair.indexOf('=');
    const rawKey = separatorIndex >= 0 ? pair.slice(0, separatorIndex) : pair;
    const rawValue = separatorIndex >= 0 ? pair.slice(separatorIndex + 1) : '';

    const key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
    if (key === paramName) {
      return decodeURIComponent(rawValue.replace(/\+/g, ' '));
    }
  }

  return null;
};

/**
 * Normalize PDF export URL by routing to appropriate backend based on inspection_module
 * @param requestUrl The request URL potentially containing /notifications/email/pdf
 * @param params Request parameters that may contain inspection_module
 * @returns The normalized URL with correct endpoint path
 */
const normalizePdfExportUrl = (requestUrl: string, params?: any): string => {
  const pdfPattern = /\/notifications\/email\/pdf(\?|$)/;
  if (!pdfPattern.test(requestUrl) || !params?.inspection_module) {
    return requestUrl;
  }

  // Prefer explicit params object; fall back to parsing from URL query string.
  // All callers embed inspection_module in the URL string, not in config.params,
  // so axios never populates config.params.inspection_module automatically.
  let inspectionModule = params?.inspection_module;
  if (!inspectionModule) {
    inspectionModule = getQueryParamValue(requestUrl, 'inspection_module');
  }

  if (!inspectionModule) {
    return requestUrl;
  }

  const targetRoute = getPdfExportRoute(inspectionModule);
  if (targetRoute) {
    return requestUrl.replace(/\/notifications\/email\/pdf/, targetRoute);
  }
  
  return requestUrl;
};


export const createApiClient = () => {
  logger.log("🟢🟢🟢 API_ROUTING_ENABLED_v3.0 🟢🟢🟢");
  logger.log("API_URL:", process.env.API_URL);
  logger.log("API_URL_:", API_URL);
  const api = axios.create({
    baseURL: process.env.API_URL ? process.env.API_URL : API_URL,
    timeout: 100000,
    // Do NOT set default Content-Type here; let axios/browser set it per request
  });

// Interceptor to include the auth token in the headers
api.interceptors.request.use(
  async (config) => {
    try {    

      
      const state = store.getState();

      // Check if user exists in state
      if (!state.auth || !state.auth.user) {
        logger.warn("No user in state, redirecting to login");
        redirectToLogin();
        return Promise.reject(new Error("Authentication required"));
      }

      let token;
      try {
        // Always acquire a fresh token to ensure it's valid for API calls
        // This ensures we always have a token with the right scopes and expiry
        logger.log("Acquiring fresh token for API request");
        token = await acquireTokenSilent(false);
        
        // Fallback to stored token if acquisition fails (shouldn't happen)
        if (!token && state.auth.user.accessToken) {
          logger.log("Using accessToken from Redux state as fallback");
          token = state.auth.user.accessToken;
        }
      } catch (tokenError) {
        console.error("Failed to acquire token:", tokenError);
        reportError({
          severity: "ERROR",
          message:
            tokenError instanceof Error
              ? tokenError.message
              : String(tokenError),
          stackTrace:
            tokenError instanceof Error ? tokenError.stack : undefined,
        });

        // Check for specific error types
        const errorMessage = String(tokenError);
        if (errorMessage.includes("AADSTS50196")) {
          logger.error(
            "Client request loop detected - stopping API request"
          );
          return Promise.reject(
            new Error("Authentication loop detected - please restart the app")
          );
        } else if (
          errorMessage.includes("user authentication required") ||
          errorMessage.includes("No accounts found")
        ) {
          logger.log("User authentication required, redirecting to login");
          redirectToLogin();
          return Promise.reject(new Error("User authentication required"));
        }

        redirectToLogin();
        return Promise.reject(new Error("Failed to acquire token"));
      }

      // Extract the token value and add it to headers
      const tokenValue = getTokenValue(token);
      if (tokenValue) {
        config.headers.Authorization = `Bearer ${tokenValue}`;

        // Cache the bearer token in SessionLockService so it is available
        // synchronously during beforeunload for lock release on tab close/refresh
        const lockSvc = getSessionLockService();
        if (lockSvc) {
          lockSvc.setAuthToken(tokenValue);
        }
      } else {
        logger.warn("No valid token available, redirecting to login");
        redirectToLogin();
        return Promise.reject(new Error("Authentication required"));
      }

      // Check if period-week API needs to be called before any API request
      // Skip this check if the current request is to the period-week endpoint itself
      if (!config.url?.includes('/common/period-week')) {
        await fetchPeriodWeekIfNeeded(api);
      }

      // Add clientTimestamp to query parameters for POST requests
      // Skip for lock-related endpoints (heartbeat, acquire, release)
      if (config.method?.toUpperCase() === 'POST' && !config.url?.includes('/common/lock')) {
        const clientTimestamp = getLocalTimestamp()
        
        // Initialize params if it doesn't exist
        if (!config.params) {
          config.params = {};
        }
        
        // Add clientTimestamp to params (will be automatically converted to query string by axios)
        config.params.clientTimestamp = clientTimestamp;
        
        logger.log('Added clientTimestamp to POST request:', clientTimestamp);
      }

      // Add common org context fields to questions and PDF export API requests
      // Matches: /questions, /questions/all, /shift-handover-walks/questions, /sparc-walks/questions, /new-hire-onboarding/probation, /notifications/email/pdf (and variants)
      // Excludes: /admin/{anymodule}/questions (e.g. kfa-management, observations, nho, racking-inspection, site-evaluation)
      //
      // Opt-out flag:
      //   _skipOrgContextForQuestions: true — skip locationType, organizationType, is3pl, status injection.
      //                                       Used when fetching questions for existing observations/handovers/sparc-walks where
      //                                       these params should not be applied.
      const questionsUrlPattern = /\/questions(\/|$|\?)/;
      const nhoQuestionsUrlPattern = /\/new-hire-onboarding\/probation/;
      const pdfExportUrlPattern = /\/notifications(?:\/nho|\/riseqa)?\/email\/pdf/;
      const kfaManagementQuestionsPattern = /\/admin\/[^/]+\/questions/;
      
      // Debug logging for questions endpoints
      if (config.url && questionsUrlPattern.test(config.url)) {
        console.log('[Interceptor] Questions endpoint detected:', config.url);
        console.log('[Interceptor] config object:', config);
        console.log('[Interceptor] _skipOrgContextForQuestions flag:', (config as any)._skipOrgContextForQuestions);
        console.log('[Interceptor] Will skip org context?', !!(config as any)._skipOrgContextForQuestions);
      }
      
      if (
        config.url &&
        !kfaManagementQuestionsPattern.test(config.url) &&
        !(config as any)._skipOrgContextForQuestions &&
        (
          questionsUrlPattern.test(config.url) ||
          nhoQuestionsUrlPattern.test(config.url) ||
          pdfExportUrlPattern.test(config.url)
        )
      ) {
        if (!config.params) {
          config.params = {};
        }
        const selectedLocation = state.appData?.selectedLocation;
        config.params.locationType = selectedLocation?.locationType;
        config.params.organizationType = selectedLocation?.organizationType;
        config.params.is3pl = selectedLocation?.is3pl
        config.params.status = state.appData?.selectedLocation?.status;
        logger.log('Added org context params to API request:', config.url, config.params);
      }

      // ── Automatic location/status context for GET /admin/role-management/roles ──
      //
      // Any GET /admin/role-management/roles call that does NOT set an opt-out flag
      // below will have the following params injected by this interceptor, so
      // individual callers (DLO thunks, useRefreshRoles, etc.) don't need to
      // duplicate this logic:
      //
      //   locationTypes     <- selectedLocation.locationType
      //   organizationTypes <- selectedLocation.organizationType
      //   threePlStatuses   <- 'Yes' | 'No'  (from selectedLocation.is3pl)
      //   statuses          <- 'Enabled'  (only if caller hasn't set it already)
      //
      // Opt-out flags (set on the Axios request config object):
      //
      //   _skipLocationContext: true  — skip ALL of the above injections.
      //                                 Used by admin-module thunks (fetchRoles,
      //                                 fetchRoleStats) that need unfiltered results
      //                                 for the admin CRUD UI.
      //
      //   _skipStatusFilter: true     — inject location params but skip the automatic
      //                                 statuses: 'Enabled' addition.
      //                                 Used by fetchAllEmployeeRoles which must
      //                                 include disabled roles so existing/submitted
      //                                 schedules can still resolve role labels.
      // ─────────────────────────────────────────────────────────────────────────────
      const rolesListUrlPattern = /\/admin\/role-management\/roles($|\?)/;
      if (
        config.url &&
        config.method?.toUpperCase() === 'GET' &&
        rolesListUrlPattern.test(config.url) &&
        !(config as any)._skipLocationContext
      ) {
        if (!config.params) {
          config.params = {};
        }
        // selectedLocation is often stored as { id, displayText } (stripped by
        // LhSidebar / DLOHeader when the user picks a location). In that case,
        // locationType / organizationType / is3pl are undefined. Fall back to
        // the full EnrichedLocation from state.appData.locations using the id.
        const rawSelected = state.appData?.selectedLocation;
        const enrichedLocations: any[] = state.appData?.locations ?? [];
        const enriched = enrichedLocations.find(
          (l: any) => String(l.id) === String(rawSelected?.id)
        );
        const loc = (enriched ?? rawSelected) as any;

        if (loc?.locationType) {
          config.params.locationTypes = loc.locationType;
        }
        if (loc?.organizationType) {
          config.params.organizationTypes = loc.organizationType;
        }
        if (loc?.is3pl != null) {
          config.params.threePlStatuses = loc.is3pl ? 'Yes' : 'No';
        }
        if (!(config as any)._skipStatusFilter && !config.params.statuses) {
          config.params.statuses = 'Enabled';
        }
        logger.log('Added location context params to roles API request:', config.url, config.params);
      }

      // Acquire session lock if this endpoint requires one
      // Skip for lock-related endpoints themselves
      if (!config.url?.includes('/common/lock')) {
        const fullUrl = config.url || '';
        const endpoint = fullUrl.split('?')[0];
        const method = (config.method || 'GET').toUpperCase();

        // Check if this endpoint requires a lock
        if (shouldAcquireLock(endpoint, method)) {
          const lockInfo = await acquireSessionLockIfNeeded(api, config);
          
          // If lock parameters could not be extracted, allow request to proceed without lock
          // This happens when required fields are missing (e.g., standup ID)
          // The endpoint may handle the operation without a lock
          if (!lockInfo) {
            logger.warn('[SessionLock] Could not acquire lock (missing parameters) - allowing request to proceed without lock');
          } else {
            // Store lock info in config for later release in response interceptor
            (config as any)._sessionLock = {
              resourceId: lockInfo.resourceId,
              lockToken: lockInfo.lockToken,
            };
          }
        }
      }

      // Normalize PDF export URLs for routing to appropriate backend endpoint
      if (config.url && config.params?.inspection_module) {
        config.url = normalizePdfExportUrl(config.url, config.params);
      }

        // Route Analytics Hub module requests to the dedicated analytics service.
        // Any endpoint starting with /analytics is served by a separate backend.
        // Temporarily disabled: keep commented for quick re-enable later.
        // if (config.url?.startsWith('/analytics')) {
        //   config.baseURL = 'http://localhost:9095/';
        //   logger.log('[Routing] Analytics Hub request — overriding baseURL to', config.baseURL);
        // }

        // TEMPORARY: Route ASN endpoints to local development server
        // TODO: Remove this line once deployed to production
        // if (config.url?.includes('/asn/filters/dropdowns') || 
        //     config.url?.includes('/asn/trend/non-asn-load-trend') || 
        //     config.url?.includes('/asn/overview/kpis') || 
        //     config.url?.includes('/asn/load-details/list') || 
        //     config.url?.includes('/asn/load-details/export') || 
        //     config.url?.includes('/asn/bottom-performing-non-asn') || 
        //     config.url?.includes('/asn/loads-vs-non-asn/chart') ||
        //     config.url?.includes('/asn/location-details') ||
        //     config.url?.includes('/asn/location-details/companion')
        //   ) {
        //     config.baseURL = 'http://localhost:9095/api';
        //     logger.log('[Routing] ASN endpoint detected — routing to localhost:9095', {
        //       endpoint: config.url,
        //       baseURL: config.baseURL
        //     });
        // }

        // Apply routing map - override baseURL for specific subpaths
        // if (config.url) {
        //   const routedBaseUrl = getBaseUrlForEndpoint(config.url);
        //   if (routedBaseUrl) {
        //     config.baseURL = routedBaseUrl;
        //     logger.log('[Routing] Override applied:', {
        //       method: config.method,
        //       url: config.url,
        //       baseURL: config.baseURL,
        //     });
        //   } else {
        //     logger.log('[Routing] No override match:', {
        //       method: config.method,
        //       url: config.url,
        //       baseURL: config.baseURL,
        //     });
        //   }
        // }
        // Local dev only: the live APIM dev gateway has no matching Load Execution
        // data for this scope, while the local backend does - route there instead.
        // if (
        //   __DEV__ &&
        //   (config.url?.includes('/ontime-dispatch/filters/available') ||
        //     config.url?.includes('/ontime-dispatch/latest-update-date') ||
        //     config.url?.includes('/ontime-dispatch/location-summary') ||
        //     config.url?.includes('/ontime-dispatch/load-execution') ||
        //     config.url?.includes('/ontime-dispatch/load-execution/{dispatchId}/sscc-breakdown') ||
        //     config.url?.includes('/ontime-dispatch/load-execution/scorecards') ||
        //     config.url?.includes('/ontime-dispatch/load-execution/export') ||
        //     config.url?.includes('/ontime-dispatch/executive-overview') ||
        //     config.url?.includes('/ontime-dispatch/executive-overview/charts') ||
        //     config.url?.includes('/ontime-dispatch/completed-trend') ||
        //     config.url?.includes('/ontime-dispatch/ontime-trend') ||
        //     config.url?.includes('/ontime-dispatch/driver-arrived-trend') ||
        //     config.url?.includes('/ontime-dispatch/completed-variance') ||
        //     config.url?.includes('/ontime-dispatch/ontime-variance') ||
        //     config.url?.includes('/ontime-dispatch/driver-arrived-variance'))
        // ) {
        //   config.baseURL = 'http://localhost:9095/api';
        //   logger.log('[Routing] ontime-dispatch endpoint detected — routing to localhost:9095', {
        //     endpoint: config.url,
        //     baseURL: config.baseURL
        //   });
        // }
      return config;
    } catch (err: any) {
      // Don't redirect to login for lock conflicts or lock acquisition failures
      // They're handled by the modal
      if ((err as any).isLockConflict || (err as any).isLockAcquisitionFailed) {
        logger.warn('Lock-related error in request interceptor - passing to response handler');
        return Promise.reject(err);
      }

      logger.error("Auth error in request interceptor:", err);
      redirectToLogin();
      return Promise.reject(err);
    }
  },
  (error) => {
    logger.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add an interceptor to handle token refresh
api.interceptors.response.use(
  async (response) => {
    // Don't auto-release lock on successful response
    // Lock will be kept alive until user navigates away from the page
    return response;
  },
  async (error) => {
    // Don't auto-release lock on error either
    // Lock will be kept alive and shown in expiration warning modal if about to expire
    const originalRequest = error?.config;

    // Some adapters/platforms (notably on iOS request setup failures) can surface
    // errors without a request config. In that case, pass the error through
    // unchanged so we don't throw here and incorrectly force a login redirect.
    if (!originalRequest) {
      logger.error('Response interceptor received error without request config:', error);
      return Promise.reject(error);
    }

    // Check if this is a GPID lookup request - if so, don't auto-redirect on 401
    const isGpidLookup = originalRequest.url?.includes('/common/employees/gpid/') || 
                        originalRequest.metadata?.isGpidLookup;

    // Check if this is a lock conflict (423) error from lock endpoints
    // Don't treat lock conflicts as auth errors - let lock error handling manage it
    const isLockConflict = originalRequest.url?.includes('/common/lock/') && 
                          error.response?.status === 423;
    
    if (isLockConflict) {
      logger.log('[SessionLock] Lock conflict (423) detected - passing to lock error handler');
      return Promise.reject(error);
    }

    // For GPID lookup 401 errors, just return the error without auth redirect
    if (isGpidLookup && error.response?.status === 401) {
      logger.log("GPID lookup 401 error - not redirecting to login");
      return Promise.reject(error);
    }

    // Check if the error is due to an expired token (for non-GPID lookup requests)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        logger.log("🔄 401 error received - attempting token refresh with forceRefresh");
        // Force refresh to ensure we get a new token
        const newToken = await acquireTokenSilent(true);
        const tokenValue = getTokenValue(newToken);

        if (tokenValue) {
          logger.log("✅ Token refresh successful, updating Redux store");
          // Update the token in the Redux store using the correct action
          store.dispatch(tokenAcquisitionSuccess(newToken));

          // Update the Authorization header and retry the original request
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${tokenValue}`;
          
          // Log the retry attempt
          logger.log("🔄 Retrying request with new token");
          return api(originalRequest);
        } else {
          logger.log("🔄 Silent token refresh failed, attempting interactive authentication");
          
          // Try interactive authentication before giving up
          try {
            const interactiveToken = await acquireTokenInteractive();
            const interactiveTokenValue = getTokenValue(interactiveToken);
            
            if (interactiveTokenValue) {
              logger.log("✅ Interactive token acquisition successful");
              // Update the token in the Redux store using the correct action
              store.dispatch(tokenAcquisitionSuccess(interactiveToken));

              // Update the Authorization header and retry the original request
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${interactiveTokenValue}`;
              
              // Log the retry attempt
              logger.log("🔄 Retrying request with interactive token");
              return api(originalRequest);
            }
          } catch (interactiveError) {
            logger.error("❌ Interactive token acquisition failed:", interactiveError);
          }

          logger.log("❌ All token acquisition methods failed, redirecting to login");
          redirectToLogin();
          return Promise.reject(new Error("Token refresh failed"));
        }
      } catch (refreshError) {
        logger.error("❌ Token refresh failed:", refreshError);
        reportError({
          severity: "ERROR",
          message:
            refreshError instanceof Error
              ? refreshError.message
              : String(refreshError),
          stackTrace:
            refreshError instanceof Error ? refreshError.stack : undefined,
        });

        // Try interactive authentication as fallback
        try {
          logger.log("🔄 Attempting interactive authentication as fallback");
          const interactiveToken = await acquireTokenInteractive();
          const interactiveTokenValue = getTokenValue(interactiveToken);
          
          if (interactiveTokenValue) {
            // Update the token in the Redux store
            store.dispatch({ 
              type: "auth/tokenAcquisitionSuccess", 
              payload: interactiveToken 
            });

            // Update the Authorization header and retry the original request
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            originalRequest.headers.Authorization = `Bearer ${interactiveTokenValue}`;
            return api(originalRequest);
          }
        } catch (interactiveError) {
          console.error("❌ Fallback interactive token acquisition failed:", interactiveError);
        }

        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    // Check if this is another type of auth error that should trigger a redirect (but not for GPID lookup or lock conflicts)
    if (!isGpidLookup && error.response?.status !== 423 && isAuthError(error)) {
      console.warn("Authentication error detected, redirecting to login");
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

// ...existing code...

  return api;
};

export const apiClient = async (
  api: ReturnType<typeof createApiClient>,
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  data?: any,
  config?: any
) => {
  try {
    // Log incoming config for debugging skipOrgContext
    if (endpoint.includes('questions')) {
      console.log('[apiClient] Received config for questions endpoint:', config);
      console.log('[apiClient] _skipOrgContextForQuestions flag:', config?._skipOrgContextForQuestions);
    }

    // Merge any provided config with default config
    const requestConfig = {
      ...config,
      url: endpoint,
      method,
      data,
    };

    // Log merged config for debugging
    if (endpoint.includes('questions')) {
      console.log('[apiClient] Merged requestConfig:', requestConfig);
      console.log('[apiClient] _skipOrgContextForQuestions in requestConfig:', requestConfig._skipOrgContextForQuestions);
    }

    // Special handling for bulk updates to admin targets
    if (
      method === "PUT" &&
      endpoint === "/nho/admin/targets" &&
      data?.updates &&
      Array.isArray(data.updates)
    ) {
      console.log(`API Request to ${endpoint} with method ${method}`);
      console.log(`Total records in bulk update: ${data.updates.length}`);

      // Make sure each update has valid fields to prevent partial updates
      data.updates.forEach((update: any, index: number) => {
        console.log(`Update record #${index + 1}:`, JSON.stringify(update));
      });

      // Log complete request for debugging
      console.log(`Full request payload: ${JSON.stringify(data)}`);
    }

    // Make the API call using the merged config
    const response = await api(requestConfig);

    // Special logging for admin targets updates
    if (method === "PUT" && endpoint === "/nho/admin/targets") {
      console.log(`API Response from ${endpoint}:`, response.data);
      console.log(`Response status: ${response.status}`);
      console.log("Response headers:", response.headers);
    }

    return response.data;
  } catch (error: any) {
    console.error("API Error:", error);
    // Check if this is an auth-related error and handle it
    if (isAuthError(error)) {
      console.warn(
        "Authentication error detected in API call, redirecting to login"
      );
      redirectToLogin();
    }
    reportError({
      severity: "ERROR",
      message: error instanceof Error ? error.message : String(error),
      stackTrace: error instanceof Error ? error.stack : undefined,
    });

    // Enhanced error logging for debugging
    if (error.response) {
      // The request was made and the server responded with an error status
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error("Request error - no response received:", error.request);
    } else {
      // Something happened in setting up the request
      console.error("Request setup error:", error.message);
    }

    throw error;
  }
};
